import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { PageLayout, PageSection } from "@/components/site/PageLayout";
import { Reveal, Label } from "@/components/site/Reveal";
import { ldScript, serviceSchema } from "@/lib/schema";
import heroEpc from "@/assets/hero-epc.jpg";

export const Route = createFileRoute("/full-service-engineering-procurement-and-construction-epc")({
  head: () => ({
    ...pageHead({
      title: "Commercial Solar EPC Services · SunSmart Engineering",
      description:
        "Full-service engineering, procurement and construction (EPC) support for businesses entering or expanding in the solar industry. Ask about your next project.",
      path: "/full-service-engineering-procurement-and-construction-epc",
    }),
    scripts: [
      ldScript(
        serviceSchema({
          name: "Commercial EPC Services",
          description:
            "Full-service Engineering, Procurement, and Construction support for businesses entering or expanding within the solar industry.",
          url: "/full-service-engineering-procurement-and-construction-epc",
          serviceType: "EPC Services",
        }),
      ),
    ],
  }),
  component: EpcPage,
});

const whyPartner = [
  {
    n: "01",
    title: "Expert Engineering & Design",
    desc: "Speed through permitting with permit-ready designs created by a team with extensive experience in solar engineering and your clients' AHJ. Our designs are optimized for performance, ensuring compliance with local codes and regulations.",
  },
  {
    n: "02",
    title: "Reliable Procurement",
    desc: "We partner with distributors to source and provide high-quality materials at competitive prices. Benefit from our established relationships with top suppliers, giving you access to the best products in the industry.",
  },
  {
    n: "03",
    title: "Professional Installation",
    desc: "Our network of trained professionals ensures installations adhere to strict quality and safety standards. Every project is executed with the goal of maximizing system performance and long-term reliability.",
  },
];

const process = [
  { n: "01", k: "Consultation & Assessment", v: "We discuss your goals, requirements, and timelines." },
  { n: "02", k: "Sales Training & Support", v: "We work with your team to ensure sold systems meet customer expectations." },
  { n: "03", k: "Engineering & Design", v: "Our team creates stamped, permit-ready plans optimized for local compliance." },
  { n: "04", k: "Procurement", v: "We source and deliver the best materials for your project." },
  { n: "05", k: "Installation", v: "Our professionals execute the project with a focus on safety, quality, and performance." },
  { n: "06", k: "Ongoing Support", v: "We provide post-installation services to ensure the continued success of your solar system." },
];

const commitments = [
  "Helping businesses deliver ethical, high-quality solar solutions.",
  "Providing comprehensive training for sales teams to ensure best practices and compliance.",
  "Ensuring your customers receive systems with high ROI and long-term performance through expertly designed and installed projects.",
];

function EpcPage() {
  return (
    <PageLayout
      kicker="Renewables & Resiliency · Commercial EPC"
      title="Engineering, Procurement & Construction (EPC) Services"
      intro="When new companies enter the solar market, challenges often arise: underpriced installations that hurt the market, permitting delays, poorly handled financing, and installation issues like electrical faults and roof leaks. We're here to change that."
    >
      {/* INTRO / B2B PARTNER STATEMENT */}
      <PageSection label="01" title="A B2B Engineering Partner">
        <div className="grid gap-16 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <Reveal>
            <div className="overflow-hidden rounded-sm border border-border">
              <img
                src={heroEpc}
                alt="Commercial building project nearing completion at golden hour"
                loading="lazy"
                className="aspect-[16/10] h-full w-full object-cover"
              />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="text-lg leading-relaxed text-muted-foreground">
                At SunSmart, we are committed to offering the best possible solutions for our
                industry and partners. Our comprehensive Engineering, Procurement, and
                Construction (EPC) services are tailored to support your business when entering
                or expanding within the solar industry. We cover everything from sales training
                for your team, to the passed final inspection.
              </p>
            </Reveal>
            <Reveal delay={100}>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                With deep combined experience and a portfolio spanning residential, commercial,
                and utility-scale solar, we are your trusted partner for delivering high-quality, sustainable
                solar solutions. SunSmart Engineering operates as a{" "}
                <span className="text-sun font-semibold">B2B partner</span>, streamlining your
                processes and delivering results that last, all without competing with your core
                operations.
              </p>
            </Reveal>
          </div>
        </div>
      </PageSection>

      {/* WHY PARTNER WITH SUNSMART */}
      <PageSection label="02" title="Why Partner with SunSmart?" theme="surface">
        <div className="grid gap-5 lg:grid-cols-3">
          {whyPartner.map((w, i) => (
            <Reveal key={w.title} delay={i * 100}>
              <div className="h-full rounded-sm border border-border bg-background p-8">
                <div className="font-display text-4xl font-black leading-none text-sun">{w.n}</div>
                <div className="mt-4 font-display text-lg font-bold uppercase">{w.title}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </PageSection>

      {/* HOW WE WORK WITH YOU */}
      <PageSection label="03" title="How We Work with You">
        <Reveal>
          <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground">
            SunSmart Engineering is your partner every step of the way. We customize our EPC
            services to fit your specific needs and ensure successful project execution. Whether
            you're new to solar or looking for a reliable partner to manage complex projects, we
            have the experience and expertise to support you.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-4 md:grid-cols-2">
          {process.map((s, i) => (
            <Reveal key={s.n} delay={(i % 4) * 90}>
              <div className="flex gap-6 rounded-sm border border-border bg-surface p-6">
                <div className="font-display text-4xl font-black leading-none text-sun">{s.n}</div>
                <div>
                  <div className="font-display text-base font-bold uppercase leading-tight">{s.k}</div>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{s.v}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={200}>
          <Link
            to="/contact"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink hover:scale-[1.03] transition-transform"
          >
            Get started <span>→</span>
          </Link>
        </Reveal>
      </PageSection>

      {/* COLLABORATING FOR SUCCESS */}
      <PageSection label="04" title="Collaborating for Success" theme="surface">
        <div className="grid gap-16 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-lg leading-relaxed text-muted-foreground">
                We understand the concerns around competition in the solar industry. Rest assured,
                we are here to <span className="text-sun font-semibold">support, not compete with, your business</span>.
                We do not sell solar services to homeowners. Instead, we provide expert EPC
                services exclusively to businesses across industries that are already in or
                planning to enter the market.
              </p>
            </Reveal>
            <Reveal delay={100}>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                Low-quality providers are increasingly entering the market, cutting corners,
                underpricing services, and causing reputational damage. This leads to stricter
                municipal regulations and diminished trust in solar solutions. SunSmart
                Engineering is committed to <span className="text-sun font-semibold">raising industry standards</span>.
              </p>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                By partnering with SunSmart, you gain access to a shared infrastructure that
                mirrors how other industries collaborate with centralized engineering and supply
                chains. Our mission is to strengthen your capabilities, protect your reputation,
                and help you deliver high-quality projects that enhance the solar industry's
                credibility.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <div className="grid gap-4">
              {commitments.map((c, i) => (
                <Reveal key={c} delay={i * 100}>
                  <div className="flex gap-4 rounded-sm border border-border bg-background p-6">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-sun" />
                    <p className="text-sm leading-relaxed text-foreground/85">{c}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </PageSection>

      {/* LET'S BUILD THE FUTURE */}
      <PageSection label="05" title="Let's Build the Future of Solar Together">
        <div className="grid gap-16 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-8">
            <p className="text-lg leading-relaxed text-muted-foreground">
              Ready to expand your business with trusted EPC services? SunSmart Engineering is
              here to help you deliver high-quality solar solutions with minimal risk and maximum
              efficiency. Contact us today to learn how we can support your next project.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink hover:scale-[1.03] transition-transform"
              >
                Get started <span>→</span>
              </Link>
            </div>
          </Reveal>
        </div>

        {/* related services */}
        <div className="mt-16 border-t border-border pt-10">
          <Label n="→" text="Related Services" />
          <div className="mt-6 flex flex-wrap gap-4">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground hover:border-sun hover:text-sun transition-colors"
            >
              All Services
            </Link>
            <Link
              to="/pv"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground hover:border-sun hover:text-sun transition-colors"
            >
              Solar Electric (PV) Design
            </Link>
            <Link
              to="/energy-storage"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground hover:border-sun hover:text-sun transition-colors"
            >
              Energy Storage Design
            </Link>
          </div>
        </div>
      </PageSection>
    </PageLayout>
  );
}
